import React, { useState, useEffect, useCallback } from "react";
import { ModalHeader, StyledModalBody } from "../../components/Modals";
import { Form, FormControl, FormField, FormItem, FormMessage } from "../../components/ui/Form";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Separator } from "../../components/ui/Separator";
import { Palette } from "../../components/utils";
import { Flex } from "../../components/ui/Flex";
import { FeatureSwitch } from "../offers/styles";
import { ButtonBodyVariant } from "../../components/ui/ButtonVariant";
import { useForm } from "react-hook-form";
import { 
  getBuildingThermostatPresets, 
  createThermostatPreset, 
  updateBuildingThermostatPresets,  
  updateThermostatHvacMode,
  updateThermostat  
} from "../../redux/actions/thermostat";
import { closeActiveModal } from "../../helper/modals";
import { SelectItem } from "../../components/ui/Select";
import { setToast } from "../../redux/actions/window";
import { yupResolver } from "@hookform/resolvers/yup";
import { SnapBackTemperatureControl, StyledLabel, StyledSelect, SubTitleText, TemperatureRangeControl, Text, ThermostatModal, SetPointRefreshFrequencyControl } from "./ThermostatComponents";
import { createThermostatSchema } from "./ThermostatValidations";
import { SettingsModalInput } from "../settings/SettingsModals";
import { requestPending, requestSuccess, requestFail } from "../../utils/status";
import { GET_BUILDING_THERMOSTAT_PRESETS, UPDATE_BUILDING_THERMOSTAT_PRESETS, CREATE_THERMOSTAT_PRESET, UPDATE_THERMOSTAT_HVAC_MODE, UPDATE_THERMOSTAT } from "../../redux/constants";
import { defaultSetPoint, defaultSetPointCelsius, defaultTemperatureInputRanges, defaultTemperatureRange, defaultTemperatureRangeCelsius, defaultTemperatureInputRangesCelsius, capitalizeFirstLetter } from "../../utils/utils";
import { isEqual } from "lodash";
import { searchThermostats } from "../../redux/actions/thermostat";

export const CustomThermostatControlModal = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [existingCustomPreset, setExistingCustomPreset] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const { modal, data } = useSelector((state) => state.headerModal);
  const {
    buildingPresets,
    buildingPresetsStatus,
    updatePresetsStatus,
    createPresetStatus,
    updateThermostatHvacModeStatus,
    updateThermostatStatus,
  } = useSelector((state) => state.thermostat);


  const { globalOrgId } = useSelector((state) => state.Organization);
  const { globalBuildingId, globalSelectedBuilding } = useSelector((state) => state.building);

  const organizationId = globalOrgId || localStorage.getItem("orgId");
  const buildingId = globalBuildingId || localStorage.getItem("building_id");
  const buildingTempUnit = globalSelectedBuilding?.config?.thermostats_temp_unit || 'F';

  const isUpdatingPresets = updatePresetsStatus === requestPending(UPDATE_BUILDING_THERMOSTAT_PRESETS);
  const isCreatingPreset = createPresetStatus === requestPending(CREATE_THERMOSTAT_PRESET);
  const isUpdatingHvacMode = updateThermostatHvacModeStatus === requestPending(UPDATE_THERMOSTAT_HVAC_MODE);
  const isUpdatingThermostat = updateThermostatStatus === requestPending(UPDATE_THERMOSTAT);
  const hasPresetsLoaded = buildingPresetsStatus === requestSuccess(GET_BUILDING_THERMOSTAT_PRESETS);
  
  const isLoading = isUpdatingPresets || isCreatingPreset || isUpdatingHvacMode || isUpdatingThermostat;


  useEffect(() => {
    if (organizationId && buildingId) {
      dispatch(getBuildingThermostatPresets({ organizationId, buildingId }));
    }
  }, [dispatch, organizationId, buildingId]);

  useEffect(() => {
    if (hasPresetsLoaded && buildingPresets?.length > 0) {
      const customPreset = buildingPresets.find(preset => preset?.type === 'custom');
      setExistingCustomPreset(customPreset);
    }
  }, [hasPresetsLoaded, buildingPresets]);

  const form = useForm({
    resolver: yupResolver(createThermostatSchema(buildingTempUnit)),
    mode: 'onSubmit',
    defaultValues: {
      enableSnapBack: false,
      snapBackTemperature: buildingTempUnit === 'C' ? defaultSetPointCelsius.occupied : defaultSetPoint.occupied,
      snapBackTemperatureAdjustment: 3,
      heatTemperatureRange: buildingTempUnit === 'C' ? defaultTemperatureInputRangesCelsius.heat : defaultTemperatureInputRanges.heat,
      coolTemperatureRange: buildingTempUnit === 'C' ? defaultTemperatureInputRangesCelsius.cool : defaultTemperatureInputRanges.cool,
      hvacMode: capitalizeFirstLetter(data.hvacMode?.toLowerCase()) || undefined,
      fanMode: capitalizeFirstLetter(data.fanMode) || undefined,
      hold: data.temperature || undefined,
      heatHoldTemperature: data.heatHoldTemperature || data.temperature,
      coolHoldTemperature: data.coolHoldTemperature || data.temperature,
    },
  });
  

  useEffect(() => {
    if (!existingCustomPreset || isUpdating) {
      return;
    }

    const snapBackTemp = Number(existingCustomPreset.snapback_setpoint) || (buildingTempUnit === 'C' ? defaultSetPointCelsius.occupied : defaultSetPoint.occupied);
    const heatRange = [
      Number(existingCustomPreset.min_heat) || (buildingTempUnit === 'C' ? defaultTemperatureInputRangesCelsius.heat[0] : defaultTemperatureInputRanges.heat[0]),
      Number(existingCustomPreset.max_heat) || (buildingTempUnit === 'C' ? defaultTemperatureInputRangesCelsius.heat[1] : defaultTemperatureInputRanges.heat[1]),
    ];
    const coolRange = [
      Number(existingCustomPreset.min_cool) || (buildingTempUnit === 'C' ? defaultTemperatureInputRangesCelsius.cool[0] : defaultTemperatureInputRanges.cool[0]),
      Number(existingCustomPreset.max_cool) || (buildingTempUnit === 'C' ? defaultTemperatureInputRangesCelsius.cool[1] : defaultTemperatureInputRanges.cool[1]),
    ];

    form.setValue("enableSnapBack", existingCustomPreset.snapback_enabled ?? false);
    form.setValue("snapBackTemperature", snapBackTemp);
    form.setValue("autoTemperatureAdjustment", Number(existingCustomPreset.snapback_refresh_frequency_hours) || 3);
    form.setValue("heatTemperatureRange", heatRange);
    form.setValue("coolTemperatureRange", coolRange);
    
    // Only set HVAC mode and related values if they haven't been modified by the user
    const currentHvacMode = form.getValues("hvacMode");
    if (!currentHvacMode || currentHvacMode === data.hvacMode?.toLowerCase()) {
      form.setValue("hvacMode", capitalizeFirstLetter(data.hvacMode?.toLowerCase()) || undefined);
    }
    
    const currentFanMode = form.getValues("fanMode");
    if (!currentFanMode || currentFanMode === data.fanMode) {
      form.setValue("fanMode", capitalizeFirstLetter(data.fanMode) || undefined);
    }
    
    const currentHold = form.getValues("hold");
    if (!currentHold || currentHold === data.temperature) {
      form.setValue("hold", data.temperature || undefined);
    }
    
    const currentHeatHold = form.getValues("heatHoldTemperature");
    if (!currentHeatHold || currentHeatHold === (data.heatHoldTemperature || data.temperature)) {
      form.setValue("heatHoldTemperature", data.heatHoldTemperature || data.temperature);
    }
    
    const currentCoolHold = form.getValues("coolHoldTemperature");
    if (!currentCoolHold || currentCoolHold === (data.coolHoldTemperature || data.temperature)) {
      form.setValue("coolHoldTemperature", data.coolHoldTemperature || data.temperature);
    }
  }, [existingCustomPreset, form, buildingTempUnit, data, isUpdating]);

  const enableSnapBackWatch = form.watch('enableSnapBack');
  const hvacModeWatch = form.watch('hvacMode')?.toLowerCase();
  const heatRange = form.watch('heatTemperatureRange');
  const coolRange = form.watch('coolTemperatureRange');

  const onSubmit = useCallback(async (values) => {
    try {
      setIsUpdating(true);
      const presetBody = {
        type: 'custom',
        snapback_enabled: values.enableSnapBack,
        snapback_setpoint: values.snapBackTemperature,
        snapback_refresh_frequency_hours: values.autoTemperatureAdjustment,
        min_max_enabled: true,
        min_heat: values.heatTemperatureRange[0],
        max_heat: values.heatTemperatureRange[1],
        min_cool: values.coolTemperatureRange[0],
        max_cool: values.coolTemperatureRange[1],
        building_id: parseInt(buildingId),
        org_id: parseInt(organizationId),
      };

      if (existingCustomPreset) {
        const currentPresetState = { ...presetBody };
        const existingPresetState = {
          type: existingCustomPreset.type,
          snapback_enabled: existingCustomPreset.snapback_enabled ?? false,
          snapback_setpoint: existingCustomPreset.snapback_setpoint,
          snapback_refresh_frequency_hours: existingCustomPreset.snapback_refresh_frequency_hours ?? 3,
          min_max_enabled: existingCustomPreset.min_max_enabled ?? true,
          min_heat: existingCustomPreset.min_heat,
          max_heat: existingCustomPreset.max_heat,
          min_cool: existingCustomPreset.min_cool,
          max_cool: existingCustomPreset.max_cool,
          building_id: parseInt(buildingId),
          org_id: parseInt(organizationId),
        };

        if (!isEqual(currentPresetState, existingPresetState)) {
          dispatch(updateBuildingThermostatPresets({
            organizationId: parseInt(organizationId),
            buildingId: parseInt(buildingId),
            body: [{
              ...currentPresetState,
              id: existingCustomPreset.id,
            }],
            success: () => dispatch(setToast({ toastShow: true, toastMessage: t('Custom preset has been successfully updated') })),
            fail: () => dispatch(setToast({ toastShow: true, toastMessage: t('Failed to update custom preset'), modal: 'errorToast' }))
          }));
        }
      } else {
        dispatch(createThermostatPreset({
          body: presetBody,
          success: () => dispatch(setToast({ toastShow: true, toastMessage: t('Custom preset has been successfully created') })),
          fail: () => dispatch(setToast({ toastShow: true, toastMessage: t('Failed to create custom preset'), modal: 'errorToast' }))
        }));
      }

      // Check if HVAC mode has changed
      const hvacModeChanged = values.hvacMode?.toLowerCase() !== data.hvacMode?.toLowerCase();
      
      // Check if thermostat settings have changed
      const currentAdvancedState = {
        fanMode: values.fanMode.toLowerCase(),
        heatHold: values.hvacMode?.toLowerCase() === 'auto' ? values.heatHoldTemperature : values.hold,
        coolHold: values.hvacMode?.toLowerCase() === 'auto' ? values.coolHoldTemperature : values.hold,
        hold: values.hold ?? data.temperature,
      };

      const existingAdvancedState = {
        fanMode: data.fanMode.toLowerCase(),
        heatHold: data.hvacMode?.toLowerCase() === 'auto' ? (data.heatHoldTemperature || data.temperature) : data.temperature,
        coolHold: data.hvacMode?.toLowerCase() === 'auto' ? (data.coolHoldTemperature || data.temperature) : data.temperature,
        hold: data.temperature,
      };

      const thermostatSettingsChanged = !isEqual(currentAdvancedState, existingAdvancedState);

      const handleUpdateSuccess = () => {
        setIsUpdating(false);
        closeActiveModal();
      };

      const updateThermostatSettings = () => {
        const thermostatBody = {
          thermostat_id: data.id,
          fan: values.fanMode?.toLowerCase(), 
          heat_temperature: Number(values.hvacMode?.toLowerCase() === 'auto' ? values.heatHoldTemperature : parseInt(values.hold)),
          cool_temperature: Number(values.hvacMode?.toLowerCase() === 'auto' ? values.coolHoldTemperature : parseInt(values.hold)),
          hvac_mode: values.hvacMode?.toLowerCase()
        };

        dispatch(updateThermostat({
          body: thermostatBody,
          success: () => {
            dispatch(setToast({ toastShow: true, toastMessage: t('All thermostat settings have been successfully updated') }));
            handleUpdateSuccess();
          },
          fail: () => {
            setIsUpdating(false);
            dispatch(setToast({toastShow: true,toastMessage: t('Failed to update thermostat settings'),modal: 'errorToast'}));
          }
        }));
      };

      if (hvacModeChanged) {
        dispatch(updateThermostatHvacMode({
          thermostatId: data.id,
          body: { hvacMode: values.hvacMode.toLowerCase() },
          success: () => {
            dispatch(setToast({ toastShow: true, toastMessage: t('HVAC mode has been successfully updated') }));
            if (thermostatSettingsChanged) {
              updateThermostatSettings();
            } else {
              handleUpdateSuccess();
            }
          },
          fail: () => {
            setIsUpdating(false);
            dispatch(setToast({toastShow: true,toastMessage: t('Failed to update HVAC mode'),modal: 'errorToast'}));
          }
        }));
      } else if (thermostatSettingsChanged) {
        updateThermostatSettings();
      } else {
        handleUpdateSuccess();
        return;
      }

    } catch (error) {
      console.error("Error updating thermostat settings:", error);
      setIsUpdating(false);
      dispatch(setToast({ toastShow: true, toastMessage: t("Error updating settings") }));
    }
  }, [dispatch, t, organizationId, globalBuildingId, existingCustomPreset, data, closeActiveModal]);


  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <ThermostatModal
          show={modal === 'CustomThermostatControlModal'}
          side
          closeOnBackdrop={false}>
          <div style={{ padding: '1.5rem' }}>
            <ModalHeader title={t('Custom')} />
          </div>

          <Separator color={Palette.BLACK_20} orientation="horizontal" />

          <StyledModalBody>
            <Flex gap="1rem" direction="column" style={{ padding: '1.5rem' }}>

              <Flex gap="3rem" justify="space-between">
                <Flex direction="column" gap="4px" >
                  <SubTitleText>
                    Snap Back Temperature
                  </SubTitleText>
                  <Text>
                    Automatically returns the thermostat to a preset temperature range after a period of inactivity or manual override.
                  </Text>
                </Flex>
                <FormField
                  control={form.control}
                  name="enableSnapBack"
                  render={({ field: { onChange, value } }) => (
                    <FeatureSwitch
                      onChange={onChange}
                      checked={value}
                      value={value}
                      shape="pill"
                      color="primary"
                    />
                  )}
                />
              </Flex>

              {enableSnapBackWatch && (
                <>
                  <SnapBackTemperatureControl 
                    name="snapBackTemperature" 
                    control={form.control} 
                    buildingTempUnit={buildingTempUnit}
                  />
                  <SetPointRefreshFrequencyControl
                    name="autoTemperatureAdjustment"
                    control={form.control}
                  />
                </>
              )}

              <Separator color={Palette.BLACK_20} orientation="horizontal" />

              <Flex direction="column" gap="1rem" style={{ width: "100%" }}>
                <Flex direction="column" gap="4px" >
                  <SubTitleText>
                    Set Min & Max Temperature
                  </SubTitleText>
                  <Text>
                    Set the minimum and maximum temperature that can be adjusted for this thermostat under Heating and Cooling mode.
                  </Text>
                </Flex>

                <Flex direction="column" gap="0.5rem" style={{ width: "100%" }}>
                  <TemperatureRangeControl 
                    name="heatTemperatureRange" 
                    label="Heat" 
                    minMax={buildingTempUnit === 'C' ? defaultTemperatureRangeCelsius.heat : defaultTemperatureRange.heat} 
                    control={form.control} 
                    buildingTempUnit={buildingTempUnit}
                  />
                  <TemperatureRangeControl 
                    name="coolTemperatureRange" 
                    label="Cool" 
                    minMax={buildingTempUnit === 'C' ? defaultTemperatureRangeCelsius.cool : defaultTemperatureRange.cool} 
                    control={form.control} 
                    buildingTempUnit={buildingTempUnit}
                  />
                </Flex>
              </Flex>

              <Separator color={Palette.BLACK_20} orientation="horizontal" />

              <Flex direction="column" gap="4px" >
                <SubTitleText>
                  Advanced Temperature Controls
                </SubTitleText>
                <Text>
                  Control HVAC mode, fan settings, and hold behavior at the unit level.
                </Text>
              </Flex>

              <>
                <FormField
                  control={form.control}
                  name="hvacMode"
                  render={({ field }) => (
                    <FormItem alt>
                      <StyledLabel>HVAC Mode</StyledLabel>
                      <FormControl>
                        <StyledSelect
                          {...field}
                          height="3rem"
                          placeholder="Select"
                        >
                          <SelectItem value={'Heat'}>Heat</SelectItem>
                          <SelectItem value={'Cool'}>Cool</SelectItem>
                          <SelectItem value={'Auto'}>Auto</SelectItem>
                          <SelectItem value={'Off'}>Off</SelectItem>
                        </StyledSelect>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="fanMode"
                  render={({ field }) => (
                    <FormItem alt>
                      <StyledLabel>Fan Mode</StyledLabel>
                      <FormControl>
                        <StyledSelect
                          {...field}
                          height="3rem"
                          placeholder="Select"
                        >
                          <SelectItem value={'On'}>On</SelectItem>
                          <SelectItem value={'Auto'}>Auto</SelectItem>
                        </StyledSelect>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Conditional temperature inputs based on HVAC mode */}
                {hvacModeWatch === 'auto' ? (
                  <>
                    <FormField
                      control={form.control}
                      name="heatHoldTemperature"
                      render={({ field }) => (
                        <FormItem alt>
                          <StyledLabel>{t('Heat Temperature')}</StyledLabel>
                          <FormControl>
                            <SettingsModalInput
                              type="number"
                              cssOverride={`
                                height: 3rem; 
                                color: #737373;
                                font-family: "Open Sans";
                                font-size: 0.875rem;
                                font-style: normal;
                                font-weight: 400;
                                line-height: 1.5rem; 
                                letter-spacing: 0rem;
                              `}
                              placeholder={t(`Enter heat temperature (${heatRange[0]}°${buildingTempUnit} - ${heatRange[1]}°${buildingTempUnit})`)}
                              autoComplete="off"
                              min={heatRange[0]}
                              max={heatRange[1]}
                              {...field}
                              onChange={(e) => {
                                const inputValue = e.target.value;
                                const value = inputValue === '' ? '' : parseInt(inputValue);
                                field.onChange(value);
                                
                                if (value === '') {
                                  form.setError('heatHoldTemperature', {
                                    type: 'manual',
                                    message: t('Temperature is required')
                                  });
                                } else if (isNaN(value) || value < heatRange[0] || value > heatRange[1]) {
                                  form.setError('heatHoldTemperature', {
                                    type: 'manual',
                                    message: t(`Temperature must be within ${heatRange[0]}°${buildingTempUnit} - ${heatRange[1]}°${buildingTempUnit}`)
                                  });
                                } else {
                                  form.clearErrors('heatHoldTemperature');
                                }
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="coolHoldTemperature"
                      render={({ field }) => (
                        <FormItem alt>
                          <StyledLabel>{t('Cool Temperature')}</StyledLabel>
                          <FormControl>
                            <SettingsModalInput
                              type="number"
                              cssOverride={`
                                height: 3rem; 
                                color: #737373;
                                font-family: "Open Sans";
                                font-size: 0.875rem;
                                font-style: normal;
                                font-weight: 400;
                                line-height: 1.5rem; 
                                letter-spacing: 0rem;
                              `}
                              placeholder={t(`Enter cool temperature (${coolRange[0]}°${buildingTempUnit} - ${coolRange[1]}°${buildingTempUnit})`)}
                              autoComplete="off"
                              min={coolRange[0]}
                              max={coolRange[1]}
                              {...field}
                              onChange={(e) => {
                                const inputValue = e.target.value;
                                const value = inputValue === '' ? '' : parseInt(inputValue);
                                field.onChange(value);
                                
                                if (value === '') {
                                  form.setError('coolHoldTemperature', {
                                    type: 'manual',
                                    message: t('Temperature is required')
                                  });
                                } else if (isNaN(value) || value < coolRange[0] || value > coolRange[1]) {
                                  form.setError('coolHoldTemperature', {
                                    type: 'manual',
                                    message: t(`Temperature must be within ${coolRange[0]}°${buildingTempUnit} - ${coolRange[1]}°${buildingTempUnit}`)
                                  });
                                } else {
                                  form.clearErrors('coolHoldTemperature');
                                }
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                ) : (
                  <FormField
                    control={form.control}
                    name="hold"
                    render={({ field }) => (
                      <FormItem alt>
                        <StyledLabel>{t('Set Temp')}</StyledLabel>
                        <FormControl>
                          <SettingsModalInput
                            type="number"
                            cssOverride={`
                              height: 3rem; 
                              color: #737373;
                              font-family: "Open Sans";
                              font-size: 0.875rem;
                              font-style: normal;
                              font-weight: 400;
                              line-height: 1.5rem; 
                              letter-spacing: 0rem;
                            `}
                            placeholder={t(`Enter desired temperature (${hvacModeWatch === 'heat' ? heatRange[0] + '°' + buildingTempUnit + ' - ' + heatRange[1] + '°' + buildingTempUnit : coolRange[0] + '°' + buildingTempUnit + ' - ' + coolRange[1] + '°' + buildingTempUnit})`)}
                            autoComplete="off"
                            min={hvacModeWatch === 'heat' ? heatRange[0] : coolRange[0]}
                            max={hvacModeWatch === 'heat' ? heatRange[1] : coolRange[1]}
                            {...field}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              const value = inputValue === '' ? '' : (inputValue ? parseInt(inputValue) : undefined);
                              field.onChange(value);
                              
                              const currentMin = hvacModeWatch === 'heat' ? heatRange[0] : coolRange[0];
                              const currentMax = hvacModeWatch === 'heat' ? heatRange[1] : coolRange[1];
                              
                              if (inputValue !== '') {
                                if (isNaN(value) || value < currentMin || value > currentMax) {
                                  form.setError('hold', {
                                    type: 'manual',
                                    message: t(`Temperature must be within ${currentMin}°${buildingTempUnit} - ${currentMax}°${buildingTempUnit}`)
                                  });
                                } else {
                                  form.clearErrors('hold');
                                }
                              } else {
                                form.setError('hold', {
                                  type: 'manual',
                                  message: t('Temperature is required')
                                });
                              }
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </>

            </Flex>
          </StyledModalBody>
          <div style={{ padding: '1.5rem' }}>
            <ButtonBodyVariant 
              style={{ width: '100%' }} 
              type="submit"
              disabled={isLoading || form.formState.errors.hold || form.formState.errors.heatHoldTemperature || form.formState.errors.coolHoldTemperature} 
            >
              {isLoading ? t('Saving...') : t('Update')}
            </ButtonBodyVariant>
          </div>
        </ThermostatModal>
      </form >
    </Form >
  );
};
